import { DataProxy } from 'apollo-cache'
import { FetchResult } from 'apollo-link'
import {
  TransferShareMutation,
  TransferShareMutationVariables,
  useTransferShareMutation,
} from 'gqlTypes'
import { useApolloClient } from 'react-apollo'
import { readShareWithProjectFragment } from 'modules/projects/operations'
import { ErrorHandler } from 'modules/tracing'
import { removeFromPaginated } from 'utils'

export interface UseTransferShareProps {
  onCompleted: (data: TransferShareMutation) => void
  onError: (message: string) => void
}

export const useShareTransferToWorkspace = (props: UseTransferShareProps) => {
  const { onCompleted, onError } = props
  const client = useApolloClient()

  const handleUpdate = (
    cache: DataProxy,
    { data }: FetchResult<TransferShareMutation>
  ) => {
    if (!data) {
      return ErrorHandler.shouldNeverHappen(
        'TransferShare response should always have data'
      )
    }

    const shareIdentifier = data.transferShare.share.identifier
    // Remove this share from cache
    removeFromPaginated(
      cache,
      { __typename: 'Share', identifier: shareIdentifier },
      key => key.includes('.shares')
    )
  }

  const [transfer, ...rest] = useTransferShareMutation({
    onCompleted,
    onError: graphqlError => {
      onError(graphqlError.message)
      ErrorHandler.shouldNeverHappen('transferShare')
    },
  })

  return [
    (variables: TransferShareMutationVariables) => {
      const cachedShare = readShareWithProjectFragment(
        client,
        variables.shareIdentifier
      )

      const isWithinSameWorkspace =
        cachedShare?.workspace.identifier === variables.workspaceIdentifier
      const isWithinSameProject =
        Boolean(variables.projectIdentifier) &&
        cachedShare?.project?.identifier === variables.projectIdentifier

      if (isWithinSameWorkspace || isWithinSameProject) {
        return
      }

      return transfer({
        variables,
        update: (...args) => handleUpdate(...args),
      })
    },
    ...rest,
  ] as const
}
